<template>
  <div class="ms-cell-editor combobox ms-cell-combobox-multiple">
    <div
      v-show="!isSelected || readOnly"
      :class="{
        'edit-disabled': readOnly,
        'ms-editor-text': editable,
        hasError: errorProvider.isValid,
      }"
    >
      <a v-show="!isEditor" class="editor-click" @click="$emit('editorClick', $event, col, colIndex)">
        <span
          class="editor-display"
          :class="{'overflow-ellipsis': propsData.display != 'chip'}"
          :title="[
            dataRow[col.dataField]
              ? $ms.format.formatData(dataRow[col.dataField], {
                  formatType: col.formatType,
                  enumName: col.enum,
                })
              : '',
          ]"
          ><div v-if="propsData.display != 'chip'" class="editor-display-value">
            {{
              dataRow[col.dataField]
                ? $ms.format.formatData(dataRow[col.dataField], {
                    formatType: col.formatType,
                    enumName: col.enum,
                  })
                : ''
            }}
          </div>
          <div
            v-else-if="propsData.display == 'chip' && chipSelectedItems.length > 0"
            v-for="(item, index) in chipSelectedItems"
            :key="index"
            class="p-multiselect-token"
          >
            <slot name="chip" :value="item">
              <span class="p-multiselect-token-label">{{ item }}</span>
            </slot>
            <!-- <slot v-if="!disabled" name="removetokenicon" class="p-multiselect-token-icon" :item="item">
                  <span class="icon24 close p-multiselect-token-icon" @click="removeOption(item, $event)" />
                </slot> -->
          </div></span
        >
      </a>

      <ms-combobox-multiple
        v-if="isEditor"
        v-model="dataRow[col.dataField]"
        :data="comboData"
        :columns="propsData.columns"
        ref="msCombobox"
        :display="propsData.display"
        :display-field="propsData.displayField"
        :value-field="propsData.valueField"
        :quick-add="propsData.quickAdd"
        :quick-search="propsData.quickSearch"
        :quick-search-param="quickSearchParam"
        :query-mode="propsData.queryMode || 'local'"
        :dropdown-width="propsData.dropdownWidth || 0"
        :is-grid-editor="true"
        :separator="propsData.separator || ';'"
        :page-size="propsData.pageSize || 10"
        v-on="listeners"
      ></ms-combobox-multiple>

      <div v-show="errorMessage && !isEditor" v-tooltip="errorMessage" class="icon24 error"></div>
    </div>
    <div v-show="isSelected && !readOnly" ref="editor" class="ms-editor control-cell"></div>
  </div>
</template>

<script setup></script>
<script>
import {computed, watch, nextTick, ref, getCurrentInstance, inject} from 'vue';
import {useCellEditor} from './CellEditorSetup.js';
import MsComboboxMultiple from '@/components/combobox/MsComboboxMultiple.vue';

export default {
  name: 'MsCellCombobox',
  components: {MsComboboxMultiple},
  props: {
    col: {},
    dataRow: {},
    rowIndex: {
      default: 0,
      type: Number,
    },
    colIndex: {
      default: 0,
      type: Number,
    },
    // Thuộc tính để cấu hình cho phép sửa trên grid hay không
    editable: {
      default: true,
      type: Boolean,
    },
    rowSelected: {
      default: null,
    },
    readOnly: {
      default: false,
      type: Boolean,
    },
    // có cho phép show text validate bên dưới cell k
    isShowText: {
      type: Boolean,
      default: false,
    },
    propsData: {
      type: Object,
      default: () => {},
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['editorClick', 'comboboxLoadData', 'msKeydown', 'pasteInput', 'update:modelValue'],
  setup(props, {emit}) {
    const {proxy} = getCurrentInstance();
    const comboData = computed(() => {
      if (props.propsData.queryMode === 'remote') {
        return props.propsData.data[props.col.dataField];
      }
      return props.propsData.data;
    });

    const chipSelectedItems = computed(() => {
      let modelValue = props.dataRow[props.col.dataField];

      if (modelValue) {
        return modelValue.split(props.propsData.separator);
      }

      return [];
    });

    /**
     * Tham số cho form quickSearch
     */
    const quickSearchParam = computed(() => {
      return {
        ...props.propsData.quickSearchParam,
        model: {
          col: props.col,
          dataRow: props.dataRow,
        }
      };
    });
    const {
      isEditor,
      errorProvider,
      isSelected,
      listeners,
      activeCell,
      inactiveCell,
      findEditor,
      getValue,
      errorMessage,
      validate,
      clearValidate,
    } = useCellEditor({
      props,
      emit,
    });

    watch(isEditor, newVal => {
      if (newVal) {
        nextTick(() => {
          findEditor();
        });
      }
    });

    const modelValueField = props.propsData.modelValueField || props.propsData.valueField;
    const msCombobox = ref({});

    const getMetaData = () => {
      return msCombobox.value?.getMetaData ? [...msCombobox.value.getMetaData()] : [];
    };

    
    const allowCopyFromExcel = inject('allowCopyFromExcel');

    /**
     * Gán giá trị cho cell
     */
    const setValue = async value => {
      const me = proxy;

      if (me.readOnly) {
        return;
      }

      let values = value.split(';');

      if (isEditor.value && typeof me.$refs.msCombobox?.setInternalText == 'function') {
        me.$refs.msCombobox.setInternalText(value);
      }

      let match = true;

      let ids = [];

      for (let index = 0; index < values.length; index++) {
        const item = values[index];
        let find = me.comboData.find(x => x[me.propsData.displayField || me.col.dataField]?.trim() == item?.trim());

        if (!find) {
          match = false;
          break;
        } else {
          ids.push(find[me.propsData.valueField || modelValueField]);
        }
      }

      if (match) {
        me.dataRow[me.col.dataField] = value;
        me.dataRow[modelValueField] = ids.join(';');
      } else {
        me.dataRow[me.col.dataField] = null;
        me.dataRow[modelValueField] = null;
      }
      me.$emit('update:modelValue', me.dataRow[me.col.dataField]);
    };

    return {
      isEditor,
      errorProvider,
      isSelected,
      listeners,
      activeCell,
      inactiveCell,
      findEditor,
      getValue,
      errorMessage,
      validate,
      clearValidate,
      modelValueField,
      comboData,
      getMetaData,
      msCombobox,
      chipSelectedItems,
      setValue,
      allowCopyFromExcel,
      quickSearchParam
    };
  },
};
</script>

<style lang="scss" scoped>
.ms-cell-editor.ms-cell-combobox-multiple {
  .editor-display {
    display: block;
    white-space: nowrap;

    .editor-display-value {
      height: 100%;
      width: 100%;
    }

    .p-multiselect-token {
      cursor: default;
      display: inline-flex;
      align-items: center;
      height: 100%;
      padding: 0.1rem 0.3rem;
      margin-right: 0.1rem;
      background: rgb(221, 241, 224);
      color: rgb(38, 142, 52);
      border-radius: 16px;
    }
  }

  .overflow-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
